import * as XLSX from 'xlsx';
import { listInvoices, getInvoiceItems } from './db/invoices';
import { listBookingsForRange } from './db/bookings';
import { listCustomers } from './db/customers';

/**
 * Export data to Excel file
 * @param {string} userId - User ID
 * @param {string} dataType - Type of data to export ('invoices', 'bookings', 'customers', 'all')
 * @param {Object} options - Additional options for filtering data
 */
export async function exportToExcel(userId, dataType = 'all', options = {}) {
  try {
    const workbook = XLSX.utils.book_new();

    // Export customers
    if (dataType === 'customers' || dataType === 'all') {
      const customers = await listCustomers(userId);
      if (customers.length > 0) {
        // Map DB customer fields to export columns. Use the user-entered `customer_id`
        // (stored in the DB as `customer_id`) instead of the internal uuid `id`.
        const customersWS = XLSX.utils.json_to_sheet(customers.map(c => ({
          'Customer ID': c.customer_id || c.customerId || c.id,
          'Customer Name': c.customer_name || c.customerName || '',
          'Contact Person': c.contact_person || c.contactPerson || '',
          'Email': c.email || '',
          // Support multiple possible mobile field names used across sample/local code
          'Phone': c.mobile || c.mobileNo || c.phone || c.contact || c.phone_number || '',
          'Address': c.address || '',
          'City': c.city || '',
          'State': c.state || c.customer_state || '',
          'Pincode': c.pincode || '',
          // Support multiple possible GST field names
          'GST Number': c.gst_no || c.gstNo || c.gstNumber || c.gst_number || '',
          'Created At': c.created_at ? new Date(c.created_at).toLocaleString() : ''
        })));
        XLSX.utils.book_append_sheet(workbook, customersWS, 'Customers');
      }
    }

    // Export bookings
    if (dataType === 'bookings' || dataType === 'all') {
      const { fromDate, toDate, customerId } = options;
      const bookings = await listBookingsForRange(userId, customerId, fromDate, toDate);
      if (bookings.length > 0) {
        const bookingsWS = XLSX.utils.json_to_sheet(bookings.map(b => ({
          'AWB Number': b.awb_no,
          'Date': b.date,
          // Prefer the customer's user-entered customer_id (e.g., CUST001) when available
          'Customer ID': b.customers?.customer_id || b.customer_id || '',
          'Receiver Name': b.receiver_name,
          'Receiver Address': b.receiver_address,
          'Pincode': b.pincode,
          'Mode': b.mode,
          'Type': b.type,
          'Weight': b.weight,
          'Quantity': b.quantity,
          'Amount': b.amount,
          'Created At': b.created_at ? new Date(b.created_at).toLocaleString() : ''
        })));
        XLSX.utils.book_append_sheet(workbook, bookingsWS, 'Bookings');
      }
    }

    // Export invoices
    if (dataType === 'invoices' || dataType === 'all') {
      const invoices = await listInvoices(userId);
      if (invoices.length > 0) {
        const invoicesWS = XLSX.utils.json_to_sheet(invoices.map(inv => ({
          'Invoice Number': inv.invoice_no,
          'Customer': inv.customers?.customer_name || inv.customer_name || '',
          'Invoice Date': inv.invoice_date,
          'From Date': inv.from_date,
          'To Date': inv.to_date,
          'Subtotal': inv.subtotal,
          'Fuel Charge': inv.fuel_charge,
          'Royalty Charge': inv.royalty_charge,
          'Docket Charge': inv.docket_charge,
          'ODA Charge': inv.oda_charge,
          'Other Charge': inv.other_charge,
          'Total Before GST': inv.total_before_gst,
          'GST %': inv.gst_percent,
          'GST Amount': inv.gst_amount,
          'Grand Total': inv.grand_total,
          'Created At': new Date(inv.created_at).toLocaleString()
        })));
        XLSX.utils.book_append_sheet(workbook, invoicesWS, 'Invoices');

        // Export invoice items for each invoice
        if (options.includeInvoiceItems) {
          const allInvoiceItems = [];
          for (const invoice of invoices) {
            const items = await getInvoiceItems(userId, invoice.id);
            items.forEach(item => {
              allInvoiceItems.push({
                'Invoice Number': invoice.invoice_no,
                'Line Number': item.line_no,
                'Date': item.date,
                'AWB Number': item.awb_no,
                'Receiver Name': item.receiver_name,
                'Receiver Address': item.receiver_address,
                'Mode': item.mode,
                'Type': item.type,
                'Weight': item.weight,
                'Quantity': item.quantity,
                'Amount': item.amount
              });
            });
          }

          if (allInvoiceItems.length > 0) {
            const invoiceItemsWS = XLSX.utils.json_to_sheet(allInvoiceItems);
            XLSX.utils.book_append_sheet(workbook, invoiceItemsWS, 'Invoice Items');
          }
        }
      }
    }

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Create download link
    const fileName = `${dataType === 'all' ? 'all-data' : dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (error) {
    console.error('Error exporting data to Excel:', error);
    throw error;
  }
}